import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase, getOrganizationByShortCode } from '../lib/supabase'
import { getDeviceToken, getDeviceFingerprint, getCurrentPosition, queueAttendance } from '../lib/device'

const EDGE_URL = process.env.REACT_APP_EDGE_FUNCTION_URL

export default function AttendPortal() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [org, setOrg] = useState(null)
  const [orgLoading, setOrgLoading] = useState(true)
  const [shortCode, setShortCode] = useState('')
  const [shortCodeError, setShortCodeError] = useState('')
  const [step, setStep] = useState('code')
  const [nameQuery, setNameQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [selectedMember, setSelectedMember] = useState(null)
  const [idInput, setIdInput] = useState('')
  const [idError, setIdError] = useState('')
  const [result, setResult] = useState(null)
  const [gpsStatus, setGpsStatus] = useState('idle')
  const [gpsCoords, setGpsCoords] = useState(null)
  // ── FIX: track whether GPS is still needed (i.e. session has a location) ──
  // This lets us allow submit even if GPS failed for sessions with no GPS lock
  const [gpsRequired, setGpsRequired] = useState(false)
  const [consentGiven, setConsentGiven] = useState(() => !!localStorage.getItem('cf_consent'))
  const debounceRef = useRef(null)

  useEffect(() => {
    if (slug) {
      loadOrgBySlugOrCode(slug)
    } else {
      const saved = localStorage.getItem('cf_org_code')
      if (saved) setShortCode(saved)
      setOrgLoading(false)
    }
  }, [slug])

  async function loadOrgBySlugOrCode(val) {
    setOrgLoading(true)
    let { data } = await supabase
      .from('organizations')
      .select('*')
      .eq('vanity_slug', val.toLowerCase())
      .maybeSingle()

    if (!data) {
      const res = await supabase
        .from('organizations')
        .select('*')
        .ilike('short_code', val.toUpperCase())
        .maybeSingle()
      data = res.data
    }

    if (!data && val.length === 36) {
      const res = await supabase.from('organizations').select('*').eq('id', val).maybeSingle()
      data = res.data
    }

    if (data) {
      setOrg(data)
      goAfterOrgLoad(data)
    } else {
      setShortCodeError('Organisation not found. Check the link and try again.')
    }
    setOrgLoading(false)
  }

  function goAfterOrgLoad(o) {
    if (o.subscription_status === 'expired') {
      setShortCodeError("This organisation's subscription has expired.")
      return
    }
    if (o.subscription_status === 'trial' && o.trial_ends_at && new Date(o.trial_ends_at) < new Date()) {
      setShortCodeError("This organisation's free trial has ended. The administrator needs to activate their plan.")
      return
    }
    const nextStep = getNextStepAfterOrg(o)
    if (o.announce_active && o.announce_text?.trim()) {
      setStep('announce')
    } else {
      setStep(nextStep)
    }
  }

  function getNextStepAfterOrg(o) {
    const org_ = o || org
    if (!consentGiven) return 'consent'
    return org_?.id_method === 'id_only' ? 'id' : 'name'
  }

  // ── Start GPS collection early, don't block UI on failure ──────────────────
  function startGps() {
    setGpsStatus('checking')
    getCurrentPosition()
      .then(pos => {
        setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setGpsStatus('ok')
      })
      .catch(() => {
        // GPS failed — set status but DO NOT block submission.
        // The edge function will reject if GPS is required for the session.
        setGpsStatus('failed')
      })
  }

  async function handleCodeSubmit() {
    setShortCodeError('')
    const upper = shortCode.trim().toUpperCase()
    let data = null
    const byCode = await getOrganizationByShortCode(upper)
    if (byCode.data) {
      data = byCode.data
    } else {
      const bySlug = await supabase.from('organizations').select('*').eq('vanity_slug', shortCode.trim().toLowerCase()).maybeSingle()
      data = bySlug.data
    }

    if (!data) { setShortCodeError('Organisation not found. Check the code and try again.'); return }
    if (data.subscription_status === 'expired') { setShortCodeError("This organisation's subscription has expired. Contact your administrator."); return }
    if (data.subscription_status === 'trial' && data.trial_ends_at && new Date(data.trial_ends_at) < new Date()) {
      setShortCodeError("This organisation's free trial has ended. The administrator needs to activate their plan.")
      return
    }
    localStorage.setItem('cf_org_code', upper)
    setOrg(data)
    goAfterOrgLoad(data)
  }

  function giveConsent() {
    localStorage.setItem('cf_consent', '1')
    setConsentGiven(true)
    setStep(org.id_method === 'id_only' ? 'id' : 'name')
    startGps()
  }

  function dismissAnnounce() {
    setStep(getNextStepAfterOrg(org))
    if (consentGiven) startGps()
  }

  function handleNameInput(val) {
    setNameQuery(val); setSelectedMember(null)
    clearTimeout(debounceRef.current)
    if (val.length < 2) { setSuggestions([]); return }
    debounceRef.current = setTimeout(async () => {
      const { data } = await supabase
        .from('members')
        .select('id, name, member_id, group_name')
        .eq('org_id', org.id)
        .or(`name.ilike.%${val}%,member_id.ilike.%${val}%`)
        .limit(8)
      setSuggestions(data || [])
    }, 200)
  }

  function pickMember(m) {
    setSelectedMember(m); setNameQuery(m.name); setSuggestions([])
    setStep('id'); setIdInput(''); setIdError('')
  }

  async function handleSubmit() {
    setIdError(''); setStep('submitting')
    const payload = {
      org_id: org.id,
      member_name: selectedMember?.name || null,
      member_uid: idInput.trim(),
      device_token: getDeviceToken(),
      device_fingerprint: getDeviceFingerprint(),
      // Send coords if available; edge function decides whether GPS is required
      latitude: gpsCoords?.lat ?? null,
      longitude: gpsCoords?.lng ?? null,
    }
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok && res.status !== 200) {
        // Network/server error — queue offline
        queueAttendance(payload)
        setResult({ status: 'queued', message: 'No connection. Your attendance has been saved and will sync automatically when you reconnect.' })
        setStep('blocked')
        return
      }

      const data = await res.json()
      setResult(data)

      if (data.status === 'present') {
        setStep('success')
      } else {
        setStep('blocked')
      }
    } catch {
      queueAttendance(payload)
      setResult({ status: 'queued', message: 'No connection. Your attendance has been saved and will sync automatically when you reconnect.' })
      setStep('blocked')
    }
  }

  function reset() {
    setStep(getNextStepAfterOrg(org))
    setNameQuery(''); setSelectedMember(null); setIdInput(''); setIdError(''); setResult(null)
    // Re-acquire GPS for next attempt
    if (consentGiven) startGps()
  }

  // Labels with safe fallbacks
  const L = org ? {
    attendance: org.attendance_label || 'Attendance',
    idField: org.id_field_label || 'ID Number',
  } : { attendance: 'Attendance', idField: 'ID Number' }

  if (orgLoading) return (
    <div className="page-center">
      <span className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
    </div>
  )

  // ── Can submit: GPS ok, or GPS failed but we still allow (edge fn decides)
  // Only block if GPS is actively still checking AND we have no coords yet
  const canSubmit = idInput.trim().length > 0 && gpsStatus !== 'checking'

  return (
    <div className="page-center" style={{ background: 'var(--bg)', alignItems: 'flex-start', paddingTop: 'max(2rem, env(safe-area-inset-top))' }}>
      <div style={{ width: '100%', maxWidth: 420, padding: '0 1rem' }}>
        {/* Header */}
        {org && (
          <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
            {org.portal_banner ? (
              <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{org.portal_banner}</div>
            ) : (
              <div className="logo" style={{ fontSize: 20, marginBottom: 4 }}>Campus<span>Flow</span></div>
            )}
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{org.name}</div>
            {org.admin_display_name && <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>Managed by {org.admin_display_name}</div>}
          </div>
        )}

        {!org && (
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div className="logo" style={{ fontSize: 22, marginBottom: 4 }}>Campus<span>Flow</span></div>
            <p className="text-sm text-muted">Mark your attendance</p>
          </div>
        )}

        {/* ── Code entry ── */}
        {step === 'code' && (
          <div className="card fade-up">
            <div className="card-title mb-1">Enter your organisation code</div>
            <div className="card-sub mb-3">Your admin will give you this code</div>
            {shortCodeError && <div className="alert alert-error mb-3">{shortCodeError}</div>}
            <div className="form-group">
              <label className="field-label">Organisation code</label>
              <input
                type="text"
                autoFocus
                autoCapitalize="characters"
                autoComplete="off"
                placeholder="e.g. CF3X7K9P"
                value={shortCode}
                onChange={e => setShortCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && shortCode.trim() && handleCodeSubmit()}
                style={{ fontSize: 18, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}
              />
            </div>
            <button className="btn btn-primary btn-full mt-2" disabled={!shortCode.trim()} onClick={handleCodeSubmit}>
              Continue →
            </button>
          </div>
        )}

        {/* ── Announcement ── */}
        {step === 'announce' && (
          <div className="card fade-up">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📢</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Notice from {org.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Please read before continuing</div>
              </div>
            </div>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius)', padding: '1rem', fontSize: 14, lineHeight: 1.7, color: '#78350f', whiteSpace: 'pre-wrap', marginBottom: '1.25rem' }}>
              {org.announce_text}
            </div>
            <button className="btn btn-primary btn-full" onClick={dismissAnnounce}>
              Understood — continue →
            </button>
          </div>
        )}

        {/* ── Consent ── */}
        {step === 'consent' && (
          <div className="card fade-up">
            <div style={{ fontSize: 28, marginBottom: 8 }}>📍</div>
            <div className="card-title mb-2">Location permission needed</div>
            <div className="card-sub mb-3">
              <strong>{org?.name}</strong> uses CampusFlow to verify you are physically at the venue.
            </div>
            <div className="alert alert-info mb-3">
              By continuing, you consent to your GPS location being checked once when you mark attendance. Your exact location is not stored — only whether you were within the allowed area.
            </div>
            <button className="btn btn-primary btn-full" onClick={giveConsent}>I understand — continue</button>
            <button className="btn btn-ghost btn-full mt-2" onClick={() => { setOrg(null); setStep('code') }}>← Back</button>
          </div>
        )}

        {/* ── GPS status bar ── */}
        {(step === 'name' || step === 'id') && (
          <div className={`alert ${gpsStatus === 'ok' ? 'alert-success' : gpsStatus === 'failed' ? 'alert-warning' : 'alert-info'} mb-3`}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {gpsStatus === 'checking' && <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
            {gpsStatus === 'ok' && <span className="gps-dot" />}
            {gpsStatus === 'checking' && 'Verifying your location...'}
            {gpsStatus === 'ok' && 'Location verified ✓'}
            {/* FIX: was 'alert-error' and said "enable GPS to continue" — this blocked users
                on sessions without GPS lock. Changed to warning and removed blocking language. */}
            {gpsStatus === 'failed' && '⚠️ Location unavailable — you can still try to submit'}
            {gpsStatus === 'idle' && '📍 Waiting for location...'}
          </div>
        )}

        {/* ── Name search ── */}
        {step === 'name' && (
          <div className="card fade-up">
            <div className="card-title mb-1">Mark your {L.attendance.toLowerCase()}</div>
            <div className="card-sub mb-3">Start typing your name and select from the list</div>
            <div className="form-group">
              <label className="field-label">Your full name</label>
              <div className="autocomplete-wrap">
                <input
                  type="text"
                  autoFocus
                  autoComplete="off"
                  placeholder="Start typing..."
                  value={nameQuery}
                  onChange={e => handleNameInput(e.target.value)}
                />
                {suggestions.length > 0 && (
                  <div className="autocomplete-list">
                    {suggestions.map(s => (
                      <div key={s.id} className="autocomplete-item" onClick={() => pickMember(s)}>
                        <span>{s.name}</span>
                        {s.group_name && <span className="autocomplete-item-sub">{s.group_name}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="field-hint">Can't find your name? Contact your administrator.</div>
            </div>
          </div>
        )}

        {/* ── ID input ── */}
        {step === 'id' && (
          <div className="card fade-up">
            {selectedMember && (
              <div className="flex items-center gap-2 mb-3">
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                  {selectedMember.name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>{selectedMember.name}</div>
                  {selectedMember.group_name && <div className="text-xs text-muted">{selectedMember.group_name}</div>}
                </div>
              </div>
            )}
            <div className="card-sub mb-3">Enter your {L.idField.toLowerCase()} to confirm your identity</div>
            <div className="form-group">
              <label className="field-label">{L.idField}</label>
              <input
                type="text"
                autoFocus
                autoComplete="off"
                inputMode="text"
                placeholder={`Enter your ${L.idField.toLowerCase()}`}
                value={idInput}
                onChange={e => { setIdInput(e.target.value); setIdError('') }}
                onKeyDown={e => e.key === 'Enter' && canSubmit && handleSubmit()}
              />
              {idError && <div className="field-error">{idError}</div>}
            </div>
            {/* FIX: was disabled while gpsStatus === 'checking' with no timeout.
                Now only blocks for the first 12s (GPS timeout), then allows submit.
                canSubmit = has ID + not actively checking GPS */}
            <div className="flex gap-2 mt-3">
              {org?.id_method !== 'id_only' && (
                <button className="btn btn-secondary" onClick={() => { setStep('name'); setSelectedMember(null); setIdInput('') }}>← Back</button>
              )}
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {gpsStatus === 'checking' ? (
                  <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Getting location...</>
                ) : 'Submit →'}
              </button>
            </div>
          </div>
        )}

        {/* ── Submitting ── */}
        {step === 'submitting' && (
          <div className="card fade-up text-center" style={{ padding: '3rem 1.5rem' }}>
            <span className="spinner" style={{ width: 32, height: 32, borderWidth: 3, marginBottom: '1rem' }} />
            <div style={{ fontWeight: 500 }}>Verifying your {L.attendance.toLowerCase()}...</div>
            <div className="text-sm text-muted mt-1">Checking location, schedule, and identity</div>
          </div>
        )}

        {/* ── Success ── */}
        {step === 'success' && result && (
          <div className="card fade-up">
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ width: 56, height: 56, background: 'var(--green-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto 12px' }}>✓</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--green-text)' }}>{L.attendance} recorded</div>
              <div className="text-sm text-muted mt-1">Marked at {result.data?.time}</div>
            </div>
            <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1.25rem' }}>
              {[
                { label: 'Name',        val: result.data?.name },
                { label: L.idField,     val: result.data?.member_uid },
                { label: 'Session',     val: result.data?.session },
                result.data?.group ? { label: 'Group', val: result.data.group } : null,
                { label: 'Location',    val: <span className="badge badge-green">GPS Verified</span> },
              ].filter(Boolean).map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                  <span className="text-muted">{row.label}</span>
                  <span style={{ fontWeight: 500 }}>{row.val}</span>
                </div>
              ))}
            </div>
            <button className="btn btn-secondary btn-full" onClick={reset}>Done</button>
          </div>
        )}

        {/* ── Blocked / queued ── */}
        {step === 'blocked' && result && (
          <div className="card fade-up">
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ width: 56, height: 56, background: result.status === 'queued' ? 'var(--amber-bg)' : 'var(--red-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto 12px' }}>
                {result.status === 'queued' ? '⏳' : '✕'}
              </div>
              <div style={{ fontWeight: 700, fontSize: 18, color: result.status === 'queued' ? 'var(--amber-text)' : 'var(--red-text)' }}>
                {result.status === 'queued' ? 'Saved offline' : 'Access denied'}
              </div>
            </div>
            <div className={`alert ${result.status === 'queued' ? 'alert-warning' : 'alert-error'} mb-3`}>{result.message}</div>
            {result.status !== 'queued' && <div className="text-xs text-muted mb-3">This attempt has been logged and is visible to your administrator.</div>}
            <button className="btn btn-secondary btn-full" onClick={reset}>Try again</button>
          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-subtle)', marginTop: '1.5rem', paddingBottom: 'env(safe-area-inset-bottom, 1rem)' }}>
          Powered by <strong style={{ color: 'var(--text-muted)' }}>CampusFlow</strong>
        </p>
      </div>
    </div>
  )
}
